import gql from 'graphql-tag';
import {useEffect, useState} from 'react';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {EMPTY_MOLFILE} from 'chemistry/ChemistryConstants';
import {ChemicalStructureDrawerValue} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {MonomerNaturalAnalogue, NucleotideType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  BenchlingQueryResult,
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ComplexPolymerAttachmentStyle,
  ComplexPolymerBondConnectionSide,
  ComplexPolymerConnection,
  ComplexPolymerConnectionType,
  ComplexPolymerType,
  connectionHash,
  EntityAssociations,
  HELMPolymerType,
  NucleotideModificationType,
  OLIGO_DUPLEX_REVERSE_STRAND_REF,
  ResolvedAttachmentInput,
  SimplePolymerGraphQL,
  StrandType,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  getPolymerTypeFromRef,
  isOligoDuplexReverseStrand,
  isPolymerBackboneStrand,
} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {
  MoleculeAttachmentForCreateQuery,
  MoleculeAttachmentForCreateQueryVariables,
} from 'modbio/complex-polymers/create/__generated/MoleculeAttachmentForCreateQuery';
import {
  NucleotideAttachmentForCreateQuery,
  NucleotideAttachmentForCreateQueryVariables,
} from 'modbio/complex-polymers/create/__generated/NucleotideAttachmentForCreateQuery';
import {
  CreateComplexPolymerMoleculeInComplexPolymerGraphQL,
  CreateComplexPolymerNucleotideInComplexPolymerGraphQL,
  CreateComplexPolymerOligoGraphQL,
} from 'modbio/complex-polymers/create/CreateComplexPolymerUtil';
import {
  MoleculeAttachmentModalQuery,
  MoleculeAttachmentModalQueryVariables,
} from 'modbio/complex-polymers/tabs/__generated/MoleculeAttachmentModalQuery';
import {
  NucleotideAttachmentModalQuery,
  NucleotideAttachmentModalQueryVariables,
} from 'modbio/complex-polymers/tabs/__generated/NucleotideAttachmentModalQuery';
import {
  ConjugateConnection,
  ConjugateConnectionMap,
} from 'modbio/complex-polymers/tabs/ComplexPolymerComponentsSummaryUtil';
import {getNaturalMonomerId} from 'molbio/monomers/MonomersUtil';

import {ModifiedNucleotideGraphQL} from '../../../../graphql-fragments/modbio/__generated/ModifiedNucleotideGraphQL';
import {
  ConcatenateMolfilesQuery,
  ConcatenateMolfilesQueryVariables,
} from './__generated/ConcatenateMolfilesQuery';
import {
  ValidateAttachmentMolfileQuery,
  ValidateAttachmentMolfileQueryVariables,
} from './__generated/ValidateAttachmentMolfileQuery';

export type NucleotideAttachmentQueryResult =
  | SkippableBenchlingQueryResult<NucleotideAttachmentModalQuery, NucleotideAttachmentModalQueryVariables>
  | SkippableBenchlingQueryResult<
      NucleotideAttachmentForCreateQuery,
      NucleotideAttachmentForCreateQueryVariables
    >;

export type MoleculeAttachmentQueryResult =
  | SkippableBenchlingQueryResult<MoleculeAttachmentModalQuery, MoleculeAttachmentModalQueryVariables>
  | SkippableBenchlingQueryResult<
      MoleculeAttachmentForCreateQuery,
      MoleculeAttachmentForCreateQueryVariables
    >;

export type ConcatMolfilesQueryResult = BenchlingQueryResult<
  ConcatenateMolfilesQuery,
  ConcatenateMolfilesQueryVariables
>;

export type MoleculeInComplexPolymerGraphQL =
  | ExtractByTypename<
      MoleculeAttachmentModalQuery['moleculeInComplexPolymer'],
      'MoleculeInComplexPolymerNode'
    >
  | CreateComplexPolymerMoleculeInComplexPolymerGraphQL;

export type NucleotideInComplexPolymerGraphQL =
  | ExtractByTypename<
      NucleotideAttachmentModalQuery['nucleotideInComplexPolymer'],
      'NucleotideInComplexPolymerNode'
    >
  | CreateComplexPolymerNucleotideInComplexPolymerGraphQL;

export type MoleculeAttachmentPointGraphQL = NonNullable<
  ExtractByTypename<MoleculeInComplexPolymerGraphQL['attachmentPoints'][number], 'AttachmentPointNode'>
>;
export type NucleotideAttachmentPointGraphQL = NonNullable<
  ExtractByTypename<NucleotideInComplexPolymerGraphQL['attachmentPoints'][number], 'AttachmentPointNode'>
>;

export type AttachmentPointGraphQL = MoleculeAttachmentPointGraphQL | NucleotideAttachmentPointGraphQL;
type MoleculeAttachmentPointOrErrorGraphQL = MoleculeInComplexPolymerGraphQL['attachmentPoints'];
type NucleotideAttachmentPointOrErrorGraphQL = NucleotideInComplexPolymerGraphQL['attachmentPoints'];

export function areAttachmentPointNodes(
  attachmentPointOrErrors: MoleculeAttachmentPointOrErrorGraphQL | NucleotideAttachmentPointOrErrorGraphQL
): attachmentPointOrErrors is
  | Array<MoleculeAttachmentPointGraphQL>
  | Array<NucleotideAttachmentPointGraphQL> {
  return (
    attachmentPointOrErrors.length === 0 || attachmentPointOrErrors[0]?.__typename === 'AttachmentPointNode'
  );
}

function isNucleotideConnectionSideUsedInConjugateConnection(
  attachmentPoint: NucleotideAttachmentPointGraphQL,
  conjugateConnection: ConjugateConnection,
  polymerRef: string,
  residueIndex: number | null
): boolean {
  return (
    (polymerRef === conjugateConnection.linkedEntityHelmReference &&
      residueIndex === conjugateConnection.linkedEntityResidueIndex &&
      attachmentPoint.modificationType === conjugateConnection.linkedEntityModificationType &&
      attachmentPoint.rGroup === conjugateConnection.linkedEntityRGroup) ||
    (polymerRef === conjugateConnection.helmReference &&
      residueIndex === conjugateConnection.residueIndex &&
      attachmentPoint.modificationType === conjugateConnection.modificationType &&
      attachmentPoint.rGroup === conjugateConnection.rGroup)
  );
}

function isMoleculeAttachmentUsedInConjugateConnection(
  attachmentPoint: MoleculeAttachmentPointGraphQL,
  conjugateConnection: ConjugateConnection,
  polymerRef: string
): boolean {
  return (
    (polymerRef === conjugateConnection.helmReference &&
      attachmentPoint.rGroup === conjugateConnection.rGroup) ||
    (polymerRef === conjugateConnection.linkedEntityHelmReference &&
      attachmentPoint.rGroup === conjugateConnection.linkedEntityRGroup)
  );
}

function isNucleotideAttachmentPoint(
  attachmentPoint: AttachmentPointGraphQL
): attachmentPoint is NucleotideAttachmentPointGraphQL {
  return attachmentPoint.hasOwnProperty('modificationType');
}

export function preprocessAttachmentPoints<T extends AttachmentPointGraphQL>(
  attachmentPoints: Array<T>,
  conjugateConnection: ConjugateConnection,
  polymerRef: string,
  residueIndex: number | null,
  connectionSideType: ConnectionSideType
): Array<T> {
  // Mark any attachment points as available if the modal is in edit mode and the attachment point has been
  // used in the select conjugate connection.
  return attachmentPoints.map((attachmentPoint) => ({
    ...attachmentPoint,
    isAvailable:
      attachmentPoint.isAvailable ||
      (isNucleotideAttachmentPoint(attachmentPoint)
        ? isNucleotideConnectionSideUsedInConjugateConnection(
            attachmentPoint,
            conjugateConnection,
            polymerRef,
            residueIndex
          )
        : isMoleculeAttachmentUsedInConjugateConnection(attachmentPoint, conjugateConnection, polymerRef)),
  }));
}

export interface SimplePolymerBase {
  type: HELMPolymerType;
  id: string;
  helmReference: string;
  name: string;
}

export interface NucleotideSimplePolymer extends SimplePolymerBase {
  type: HELMPolymerType.RNA;
  bases: string;
  modifiedNucleotides: Array<ModifiedNucleotideGraphQL>;
  nucleotideType: NucleotideType;
}

export interface ChemSimplePolymer extends SimplePolymerBase {
  type: HELMPolymerType.CHEM;
}

export interface PeptideSimplePolymer extends SimplePolymerBase {
  type: HELMPolymerType.PEPTIDE;
}

// Class used by the create and edit pathways rather than SimplePolymerGraphQL because the create pathway
// doesn't hold component information as SimplePolymerGraphQL (since it does not go through the standard resolvers)
export type SimplePolymer = NucleotideSimplePolymer | ChemSimplePolymer | PeptideSimplePolymer;

export function getPolymerByRef(ref: string, simplePolymers: Array<SimplePolymer>): SimplePolymer {
  const polymer = simplePolymers.find((polymer) => polymer.helmReference === ref);
  assert(polymer != null, 'Expected matching polymer');
  return polymer;
}

export enum ConnectionType {
  COMPONENT_TO_BACKBONE = 'COMPONENT_TO_BACKBONE',
  COMPONENT_TO_COMPONENT = 'COMPONENT_TO_COMPONENT',
}

export enum ConnectionSideType {
  SOURCE = 'SOURCE',
  TARGET = 'TARGET',
}

// ComplexPolymerConnection requires each connection side to have a polymer_ref defined
// Since the attachment modal can be in a state where no source component is selected, we default to these
// values for COMPONENT_TO_BACKBONE and COMPONENT_TO_COMPONENT connections respectively
export const DEFAULT_BACKBONE_SOURCE_POLYMER_REF = 'RNA1';
export const PLACEHOLDER_CONJUGATE_SOURCE_POLYMER_REF = 'NO_SELECTION';

function getAttachConjugateDefaultConnection(
  polymerRef: string,
  conjugateConnectionMap: ConjugateConnectionMap
): ComplexPolymerConnection {
  const conjugateConnection = conjugateConnectionMap[polymerRef];
  // Default to existing attachment style if editing an existing connection, or drawn style attachments if
  // chemistry is enabled. Otherwise, default to leaving-group style connections.
  const attachmentStyle =
    conjugateConnection?.attachmentStyle ??
    (BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE
      ? ComplexPolymerAttachmentStyle.DRAWN
      : ComplexPolymerAttachmentStyle.LEAVING_GROUP);
  const sourceRef = conjugateConnection?.linkedEntityHelmReference ?? DEFAULT_BACKBONE_SOURCE_POLYMER_REF;
  const sourceType = getPolymerTypeFromRef(sourceRef);

  return {
    attachment_style: attachmentStyle,
    connection_type: ComplexPolymerConnectionType.COVALENT_BOND,
    target: {
      polymer_ref: polymerRef,
      residue_index: conjugateConnection?.residueIndex ?? null,
      modification_type:
        getPolymerTypeFromRef(polymerRef) === HELMPolymerType.CHEM
          ? null
          : (conjugateConnection?.modificationType ?? NucleotideModificationType.SUGAR),
      r_group: conjugateConnection?.rGroup ?? null,
    },
    source: {
      polymer_ref: sourceRef,
      residue_index:
        sourceType === HELMPolymerType.CHEM ? null : (conjugateConnection?.linkedEntityResidueIndex ?? null),
      modification_type:
        sourceType === HELMPolymerType.CHEM
          ? null
          : (conjugateConnection?.linkedEntityModificationType ?? NucleotideModificationType.SUGAR),
      r_group: conjugateConnection?.linkedEntityRGroup ?? null,
    },
  };
}

function getAttachConjugateDefaultResolvedAttachmentMolfile(
  isEditMode: boolean,
  polymerRef: string,
  conjugateConnectionMap: ConjugateConnectionMap,
  attachmentStyle?: ComplexPolymerAttachmentStyle | null
): ChemicalStructureDrawerValue {
  const conjugateConnection = isEditMode ? conjugateConnectionMap[polymerRef] : null;

  return attachmentStyle === ComplexPolymerAttachmentStyle.DRAWN
    ? (conjugateConnection?.attachmentMolfile ?? null)
    : null;
}

export const VALIDATE_MOLFILE_QUERY = gql`
  query ValidateAttachmentMolfileQuery($molfile: StrictString!)
  @engComponent(name: MODIFIED_BIOLOGICS)
  @skipAudit {
    drawnAttachmentValidationError(molfile: $molfile)
  }
`;

export type UpdateConnectionFunc = (
  connectionSide: ConnectionSideType,
  connectionSideData: Partial<ComplexPolymerBondConnectionSide>,
  attachmentStyle?: ComplexPolymerAttachmentStyle | null
) => void;

export interface AttachConjugateState {
  connection: ComplexPolymerConnection;
  setConnection: (connection: ComplexPolymerConnection) => void;
  updateConnection: UpdateConnectionFunc;
  showSourceComponentSettings: boolean;
  setShowSourceComponentSettings: (shouldShow: boolean) => void;
  showTargetComponentSettings: boolean;
  setShowTargetComponentSettings: (shouldShow: boolean) => void;
  attachmentMolfile: ChemicalStructureDrawerValue;
  setAttachmentMolfile: (attachmentMolfile: ChemicalStructureDrawerValue) => void;
  attachmentMolfileError: string | null;
}

export function useAttachConjugateState(
  isEditMode: boolean,
  startingConjugateRef: string,
  conjugateConnectionMap: ConjugateConnectionMap
): AttachConjugateState {
  const defaultConnection = getAttachConjugateDefaultConnection(startingConjugateRef, conjugateConnectionMap);
  const defaultMolfile = getAttachConjugateDefaultResolvedAttachmentMolfile(
    isEditMode,
    startingConjugateRef,
    conjugateConnectionMap,
    defaultConnection.attachment_style
  );

  const [connection, setConnection] = useState(defaultConnection);

  const [showSourceComponentSettings, setShowSourceComponentSettings] = useState(true);
  const [showTargetComponentSettings, setShowTargetComponentSettings] = useState(true);

  const [attachmentMolfile, setAttachmentMolfile] = useState<ChemicalStructureDrawerValue>(defaultMolfile);
  const [attachmentMolfileError, setAttachmentMolfileError] = useState<string | null>(null);

  const validationResult = useSkippableBenchlingQuery<
    ValidateAttachmentMolfileQuery,
    ValidateAttachmentMolfileQueryVariables
  >(VALIDATE_MOLFILE_QUERY, {
    variables: {molfile: attachmentMolfile!},
    skip: attachmentMolfile == null,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (validationResult.type === QueryResultType.SUCCESS) {
      setAttachmentMolfileError(validationResult.data.drawnAttachmentValidationError);
    }
  }, [validationResult]);

  function shouldInvalidateAttachmentMolfile(
    oldConnectionSide: ComplexPolymerBondConnectionSide,
    update: Partial<ComplexPolymerBondConnectionSide>
  ): boolean {
    const newConnectionSide = {...oldConnectionSide, ...update};
    const {
      polymer_ref: oldRef,
      residue_index: oldResidueIndex,
      modification_type: oldModType,
    } = oldConnectionSide;

    const {
      polymer_ref: newRef,
      residue_index: newResidueIndex,
      modification_type: newModType,
    } = newConnectionSide;

    return oldRef !== newRef || oldResidueIndex !== newResidueIndex || oldModType !== newModType;
  }

  function updateConnection(
    connectionSide: ConnectionSideType,
    connectionSideData: Partial<ComplexPolymerBondConnectionSide>,
    attachmentStyle: ComplexPolymerAttachmentStyle | null = null
  ): void {
    if (connectionSide === ConnectionSideType.SOURCE) {
      setConnection({
        ...connection,
        attachment_style: attachmentStyle ?? connection.attachment_style,
        source: {...connection.source, ...connectionSideData},
      });
      if (shouldInvalidateAttachmentMolfile(connection.source, connectionSideData)) {
        setAttachmentMolfile(null);
      }
    } else {
      setConnection({
        ...connection,
        attachment_style: attachmentStyle ?? connection.attachment_style,
        target: {...connection.target, ...connectionSideData},
      });
      if (shouldInvalidateAttachmentMolfile(connection.target, connectionSideData)) {
        setAttachmentMolfile(null);
      }
    }
  }

  return {
    connection,
    setConnection,
    updateConnection,
    showSourceComponentSettings,
    setShowSourceComponentSettings,
    showTargetComponentSettings,
    setShowTargetComponentSettings,
    attachmentMolfile,
    setAttachmentMolfile,
    attachmentMolfileError,
  };
}

export function getConnectionType(
  connection: ComplexPolymerConnection,
  complexPolymerType: ComplexPolymerType
): ConnectionType {
  return isPolymerBackboneStrand(complexPolymerType, connection.source.polymer_ref)
    ? ConnectionType.COMPONENT_TO_BACKBONE
    : ConnectionType.COMPONENT_TO_COMPONENT;
}

export function getStrandType(
  connection: ComplexPolymerConnection,
  complexPolymerType: ComplexPolymerType
): StrandType | null {
  return complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX
    ? connection.source.polymer_ref === OLIGO_DUPLEX_REVERSE_STRAND_REF
      ? StrandType.REVERSE
      : StrandType.FORWARD
    : null;
}

export function preprocessAttachmentPointAvailabilityFromConjugateConnections<
  T extends AttachmentPointGraphQL,
>(
  polymerRef: string,
  attachmentPoints: Array<T>,
  conjugateConnectionMap: ConjugateConnectionMap,
  connectionSideType: ConnectionSideType,
  residueIndex?: number
): Array<T> {
  // For complex polymer creation and edits through the drag-and-drop UI, all attachment points are stored in
  // state on the frontend before submission. This means that the attachment points returned from the GraphQL
  // resolvers may not have the correct availability for an attachment point. Therefore, we need to update
  // each attachment point based on the connections saved on the complex polymer so far
  const associatedConjugateConnections = Object.entries(conjugateConnectionMap)
    .map(([_, conjugateConnection]) => conjugateConnection)
    .filter(
      (conjugateConnection) =>
        conjugateConnection.helmReference === polymerRef ||
        conjugateConnection.linkedEntityHelmReference === polymerRef
    );

  return attachmentPoints.map((attachmentPoint) => {
    // On create or in the drag-and-drop UI, an attachment point an only be unavailable if it's already used
    // in a connection, as designated in a conjugate connection, or used in a nucleotide bond.
    const isUsedInConjugateConnection = associatedConjugateConnections.some((conjugateConnection) =>
      isNucleotideAttachmentPoint(attachmentPoint)
        ? isNucleotideConnectionSideUsedInConjugateConnection(
            attachmentPoint,
            conjugateConnection,
            polymerRef,
            residueIndex!
          )
        : isMoleculeAttachmentUsedInConjugateConnection(attachmentPoint, conjugateConnection, polymerRef)
    );
    const isInNucleotideBond =
      isNucleotideAttachmentPoint(attachmentPoint) && attachmentPoint.isInNucleotideBond;
    const isAvailable = !isUsedInConjugateConnection && !isInNucleotideBond;

    return {...attachmentPoint, isAvailable};
  });
}

export interface CompleteConjugateConnection extends ConjugateConnection {
  attachmentStyle: ComplexPolymerAttachmentStyle;
  linkedEntityHelmReference: string;
}

export function isCompleteConjugateConnection(
  conjugateConnection: ConjugateConnection
): conjugateConnection is CompleteConjugateConnection {
  return conjugateConnection.attachmentStyle != null && conjugateConnection.linkedEntityHelmReference != null;
}

export function getComplexPolymerConnectionFromConjugateConnection(
  conjugateConnection: CompleteConjugateConnection
): ComplexPolymerConnection {
  return {
    connection_type: ComplexPolymerConnectionType.COVALENT_BOND,
    attachment_style: conjugateConnection.attachmentStyle,
    source: {
      polymer_ref: conjugateConnection.linkedEntityHelmReference,
      residue_index:
        getPolymerTypeFromRef(conjugateConnection.linkedEntityHelmReference) === HELMPolymerType.RNA
          ? conjugateConnection.linkedEntityResidueIndex!
          : null,
      modification_type: conjugateConnection.linkedEntityModificationType,
      r_group: conjugateConnection.linkedEntityRGroup,
    },
    target: {
      polymer_ref: conjugateConnection.helmReference,
      residue_index: conjugateConnection.residueIndex,
      modification_type: conjugateConnection.modificationType,
      r_group: conjugateConnection.rGroup,
    },
  };
}

function connectionShouldBeInResolvedAttachment(
  connection: ComplexPolymerConnection,
  resolvedAttachment: ResolvedAttachmentInput
): boolean {
  // A connection should belong to a resolved attachment if:
  // - The connection is one of the connections in the attachment OR
  // - The connection is connected to one of the connections in the attachment.
  // One of these is true if:
  // - The connection references the same index/monomer on a backbone as a connection in the attachment.
  // - The connection references the same non-backbone component as a connection in the attachment.
  // Since index and monomer are always null on non-backbone components in connections,
  // we can simplify this to:
  // - One side of the connection is equal to a side of some connection in the attachment, excluding r-groups.

  function connectionSideHash(connectionSide: ComplexPolymerBondConnectionSide): string {
    return `${connectionSide.polymer_ref}${connectionSide.residue_index}${connectionSide.modification_type}`;
  }

  const involvedConnectionHashes = new Set(
    resolvedAttachment.connections_involved.flatMap((ci) => [
      connectionSideHash(ci.source),
      connectionSideHash(ci.target),
    ])
  );

  return (
    involvedConnectionHashes.has(connectionSideHash(connection.source)) ||
    involvedConnectionHashes.has(connectionSideHash(connection.target))
  );
}

export function findResolvedAttachmentForConnection(
  connection: ComplexPolymerConnection,
  resolvedAttachments: Array<ResolvedAttachmentInput>
): ResolvedAttachmentInput | null {
  return (
    resolvedAttachments.find((resolvedAttachment) =>
      connectionShouldBeInResolvedAttachment(connection, resolvedAttachment)
    ) ?? null
  );
}

function getResolvedAttachmentForComponent(
  helmReference: string,
  resolvedAttachments: Array<ResolvedAttachmentInput>,
  helmPolymerType: HELMPolymerType,
  residueIndex?: number,
  modificationType?: NucleotideModificationType
): ResolvedAttachmentInput | null {
  const resolvedAttachment = resolvedAttachments.find((resolvedAttachment) =>
    resolvedAttachment.connections_involved.some((connection) => {
      if (helmPolymerType === HELMPolymerType.RNA) {
        return (
          (connection.source.polymer_ref === helmReference &&
            connection.source.residue_index === residueIndex &&
            connection.source.modification_type === modificationType) ||
          (connection.target.polymer_ref === helmReference &&
            connection.target.residue_index === residueIndex &&
            connection.target.modification_type === modificationType)
        );
      } else {
        return (
          connection.source.polymer_ref === helmReference || connection.target.polymer_ref === helmReference
        );
      }
    })
  );
  return resolvedAttachment ?? null;
}

function getResolvedAttachmentForConnectionSide(
  connectionSide: ComplexPolymerBondConnectionSide,
  resolvedAttachments: Array<ResolvedAttachmentInput>
): ResolvedAttachmentInput | null {
  return getResolvedAttachmentForComponent(
    connectionSide.polymer_ref,
    resolvedAttachments,
    getPolymerTypeFromRef(connectionSide.polymer_ref),
    connectionSide.residue_index ?? undefined,
    connectionSide.modification_type ?? undefined
  );
}

export function getResolvedAttachmentsInputsForSubmit(
  isEditMode: boolean,
  resolvedAttachments: Array<ResolvedAttachmentInput>,
  connection: ComplexPolymerConnection,
  attachmentMolfile: string | null
): Array<ResolvedAttachmentInput> {
  // Resolved attachments for leaving group style connections (new and existing) are created on the backend
  // on submission, so we remove them here to prevent duplicates.
  const resolvedAttachmentInputsForSubmit = resolvedAttachments.filter((resolvedAttachment) =>
    resolvedAttachment.connections_involved.every(
      (c) => c.attachment_style === ComplexPolymerAttachmentStyle.DRAWN
    )
  );

  if (attachmentMolfile == null) {
    return resolvedAttachmentInputsForSubmit;
  }

  if (isEditMode) {
    const matchingIndex = resolvedAttachmentInputsForSubmit.findIndex((resolvedAttachment) =>
      resolvedAttachment.connections_involved.some(
        (connectionInvolved) => connectionHash(connectionInvolved) === connectionHash(connection)
      )
    );
    assert(matchingIndex !== -1, 'Expected there to be a resolved attachment index');
    const matchingResolvedAttachment = resolvedAttachmentInputsForSubmit[matchingIndex];
    resolvedAttachmentInputsForSubmit.splice(matchingIndex, 1, {
      molfile: attachmentMolfile,
      connections_involved: matchingResolvedAttachment.connections_involved,
    });
  } else {
    // If either of the connection component sides are in a resolved attachment, update the resolved
    // attachment with the new molfile and additional connection.
    const sourceResolvedAttachment = getResolvedAttachmentForConnectionSide(
      connection.source,
      resolvedAttachmentInputsForSubmit
    );
    const targetResolvedAttachment = getResolvedAttachmentForConnectionSide(
      connection.target,
      resolvedAttachmentInputsForSubmit
    );
    const existingResolvedAttachmentsInputs = [sourceResolvedAttachment, targetResolvedAttachment].filter(
      (resolvedAttachment) => resolvedAttachment != null
    );
    if (existingResolvedAttachmentsInputs.length > 0) {
      const connectionsInvolved = [connection];
      for (const existingResolvedAttachment of existingResolvedAttachmentsInputs) {
        const index = resolvedAttachmentInputsForSubmit.findIndex((resolvedAttachment) =>
          isEqual(resolvedAttachment, existingResolvedAttachment)
        );
        assert(index !== -1, 'Expected there to be a resolved attachment index');
        resolvedAttachmentInputsForSubmit.splice(index, 1);
        connectionsInvolved.push(...existingResolvedAttachment.connections_involved);
      }
      resolvedAttachmentInputsForSubmit.push({
        molfile: attachmentMolfile,
        connections_involved: connectionsInvolved,
      });
    } else {
      resolvedAttachmentInputsForSubmit.push({
        molfile: attachmentMolfile,
        connections_involved: [connection],
      });
    }
  }
  return resolvedAttachmentInputsForSubmit;
}

function maybeGetMonomerId(
  oligo: NucleotideSimplePolymer,
  modificationType: NucleotideModificationType,
  residueIndex: number
): string | null {
  const modifiedNucleotide = oligo.modifiedNucleotides.find(
    (modifiedNucleotide) => modifiedNucleotide.position === residueIndex
  );

  const {bases, nucleotideType} = oligo;
  // If modified nucleotide exists at that location, get the monomer ID from the modified nucleotide
  if (modifiedNucleotide != null) {
    switch (modificationType) {
      case NucleotideModificationType.SUGAR:
        return modifiedNucleotide.sugarMonomerId;
      case NucleotideModificationType.BASE:
        return modifiedNucleotide.baseMonomerId;
      case NucleotideModificationType.PHOSPHATE:
        if (modifiedNucleotide.phosphateMonomerId == null) {
          return null;
        } else {
          return modifiedNucleotide.phosphateMonomerId;
        }
      default:
        return null;
    }
  }
  // If no modified nucleotide exists at that location, find the natural monomer associated with modification
  // type/nucleotide type/base
  const base = bases.charAt(residueIndex).toUpperCase() as keyof typeof MonomerNaturalAnalogue;
  switch (modificationType) {
    case NucleotideModificationType.SUGAR:
      if (nucleotideType === NucleotideType.DNA) {
        return getNaturalMonomerId(MonomerNaturalAnalogue.D_LOWER);
      } else {
        return getNaturalMonomerId(MonomerNaturalAnalogue.R_LOWER);
      }
    case NucleotideModificationType.BASE:
      return getNaturalMonomerId(MonomerNaturalAnalogue[base]);
    case NucleotideModificationType.PHOSPHATE:
      return getNaturalMonomerId(MonomerNaturalAnalogue.P_LOWER);
    default:
      return null;
  }
}

function getConcatMolfilesVarsForConnectionSide(
  connectionSide: ComplexPolymerBondConnectionSide,
  resolvedAttachments: Array<ResolvedAttachmentInput>,
  simplePolymers: Array<SimplePolymer>
): ConcatenateMolfilesQueryVariables {
  const conjugateResolvedAttachment = getResolvedAttachmentForComponent(
    connectionSide.polymer_ref,
    resolvedAttachments,
    getPolymerTypeFromRef(connectionSide.polymer_ref),
    connectionSide.residue_index ?? undefined,
    connectionSide.modification_type ?? undefined
  );
  if (conjugateResolvedAttachment != null) {
    return {monomerOrMoleculeIds: [], molfiles: [conjugateResolvedAttachment.molfile]};
  }
  const simplePolymer = simplePolymers.find(
    (polymer) => polymer.helmReference === connectionSide.polymer_ref
  );
  assert(simplePolymer != null, 'Expected matching polymer to exist');
  const monomerOrMoleculeId =
    simplePolymer.type === HELMPolymerType.RNA
      ? connectionSide.modification_type != null && connectionSide.residue_index != null
        ? maybeGetMonomerId(simplePolymer, connectionSide.modification_type, connectionSide.residue_index)
        : null
      : simplePolymer.id;
  if (monomerOrMoleculeId != null) {
    return {monomerOrMoleculeIds: [monomerOrMoleculeId], molfiles: []};
  }
  return {monomerOrMoleculeIds: [], molfiles: []};
}

export function getConcatenateMolfilesVariables(
  simplePolymers: Array<SimplePolymer>,
  connection: ComplexPolymerConnection,
  resolvedAttachments: Array<ResolvedAttachmentInput>
): ConcatenateMolfilesQueryVariables {
  // If a resolved attachment exists that matches both sides of a connection, return its molfile.
  const maybeResolvedAttachment = resolvedAttachments.find((resolvedAttachment) =>
    resolvedAttachment.connections_involved.some(
      (connectionInvolved) => connectionHash(connectionInvolved) === connectionHash(connection)
    )
  );
  if (maybeResolvedAttachment != null) {
    return {monomerOrMoleculeIds: [], molfiles: [maybeResolvedAttachment.molfile]};
  }

  const {monomerOrMoleculeIds, molfiles} = getConcatMolfilesVarsForConnectionSide(
    connection.target,
    resolvedAttachments,
    simplePolymers
  );

  if (connection.source.polymer_ref !== PLACEHOLDER_CONJUGATE_SOURCE_POLYMER_REF) {
    const {monomerOrMoleculeIds: sourceIds, molfiles: sourceMolfiles} =
      getConcatMolfilesVarsForConnectionSide(connection.source, resolvedAttachments, simplePolymers);
    return {
      monomerOrMoleculeIds: [...monomerOrMoleculeIds, ...sourceIds],
      molfiles: [...(molfiles ?? []), ...(sourceMolfiles ?? [])],
    };
  }
  return {monomerOrMoleculeIds, molfiles};
}

export type ModificationTypeToMonomerName = {
  [id in NucleotideModificationType]?: string;
};

export function connectionSideNotFullySpecified(connectionSide: ComplexPolymerBondConnectionSide): boolean {
  const nucleotideSequenceNotFullySpecified =
    connectionSide.residue_index == null || connectionSide.modification_type == null;
  return (
    connectionSide.polymer_ref === PLACEHOLDER_CONJUGATE_SOURCE_POLYMER_REF ||
    (getPolymerTypeFromRef(connectionSide.polymer_ref) === HELMPolymerType.RNA &&
      nucleotideSequenceNotFullySpecified)
  );
}

export function shouldDisableAttachButton(
  connection: ComplexPolymerConnection,
  attachmentMolfile: string | null,
  attachmentMolfileError: string | null
): boolean {
  if (connection.attachment_style === ComplexPolymerAttachmentStyle.DRAWN) {
    return (
      connectionSideNotFullySpecified(connection.source) ||
      connectionSideNotFullySpecified(connection.target) ||
      attachmentMolfile === EMPTY_MOLFILE ||
      attachmentMolfile == null ||
      attachmentMolfileError != null
    );
  }
  return (
    connectionSideNotFullySpecified(connection.source) ||
    connectionSideNotFullySpecified(connection.target) ||
    connection.source.r_group == null ||
    connection.target.r_group == null
  );
}

export function getPolymersForAttachmentOnCreate(
  complexPolymerType: ComplexPolymerType,
  oligoPolymers: Array<CreateComplexPolymerOligoGraphQL>,
  conjugateConnectionMap: ConjugateConnectionMap,
  entityAssociations: EntityAssociations
): Array<SimplePolymer> {
  const polymers: Array<SimplePolymer> = oligoPolymers.flatMap((oligo) => {
    const polymerRefs = entityAssociations[oligo.id];
    return polymerRefs.map((ref) => ({
      type: HELMPolymerType.RNA,
      id: oligo.id,
      helmReference: ref,
      name: oligo.name,
      bases:
        complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX && isOligoDuplexReverseStrand(ref)
          ? oligo.bases.split('').reverse().join('')
          : oligo.bases,
      modifiedNucleotides:
        complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX && isOligoDuplexReverseStrand(ref)
          ? oligo.modifiedNucleotides.map((mod) => ({
              ...mod,
              position: oligo.bases.length - 1 - mod.position,
            }))
          : oligo.modifiedNucleotides,
      nucleotideType: oligo.nucleotideType,
    }));
  });

  polymers.push(
    ...Object.values(conjugateConnectionMap).map((conjugateConnection) => {
      // @ts-expect-error The polymer type assignment below is erroring with HELMPolymerType not being assignable
      // to HELMPolymerType.PEPTIDE. This is incorrect, as SimplePolymer.type is typed as HELMPolymerType
      const polymer: SimplePolymer = {
        type: getPolymerTypeFromRef(conjugateConnection.helmReference),
        id: conjugateConnection.entityId!,
        helmReference: conjugateConnection.helmReference,
        name: conjugateConnection.entityName,
      };
      return polymer;
    })
  );

  return polymers;
}

export function getPolymersForAttachmentOnEdit(
  simplePolymers: Array<SimplePolymerGraphQL>
): Array<SimplePolymer> {
  return simplePolymers.map((polymer): SimplePolymer => {
    assert(!isInaccessible(polymer.entity), 'Expected accessible polymer');
    const polymerData = {
      type: polymer.polymerType,
      id: polymer.entity.id,
      helmReference: polymer.helmReference,
      name: polymer.entity.name,
    };
    if (polymer.entity.__typename === 'SequenceNode') {
      return {
        ...polymerData,
        bases: polymer.entity.bases,
        modifiedNucleotides: polymer.entity.modifiedNucleotides,
        nucleotideType: polymer.entity.nucleotideType,
      } as NucleotideSimplePolymer;
    }
    return polymerData as ChemSimplePolymer;
  });
}

export function getSelectedPolymerRef(
  connectionSideType: ConnectionSideType,
  connection: ComplexPolymerConnection
): string {
  return connectionSideType === ConnectionSideType.SOURCE
    ? connection.source.polymer_ref
    : connection.target.polymer_ref;
}

export function getConnectionSide(
  connectionSideType: ConnectionSideType,
  connection: ComplexPolymerConnection
): ComplexPolymerBondConnectionSide {
  return connectionSideType === ConnectionSideType.SOURCE ? connection.source : connection.target;
}

function doesConnectionSideMatchConjugateConnectionForResolvedAttachment(
  complexPolymerType: ComplexPolymerType,
  connectionSide: ComplexPolymerBondConnectionSide,
  conjugateConnection: ConjugateConnection
): boolean {
  // Determines whether the connection details that identify the associated resolved attachment for the
  // connection match any conjugate connection. For backbone strands, resolved attachments are unique for each
  // monomer. For all conjugates, only the polymer needs to specified.
  if (isPolymerBackboneStrand(complexPolymerType, connectionSide.polymer_ref)) {
    return (
      (conjugateConnection.helmReference === connectionSide.polymer_ref &&
        conjugateConnection.residueIndex === connectionSide.residue_index &&
        conjugateConnection.modificationType === connectionSide.modification_type) ||
      ((conjugateConnection.linkedEntityHelmReference ?? null) === connectionSide.polymer_ref &&
        (conjugateConnection.linkedEntityResidueIndex ?? null) === connectionSide.residue_index &&
        conjugateConnection.linkedEntityModificationType === connectionSide.modification_type)
    );
  }

  return (
    conjugateConnection.helmReference === connectionSide.polymer_ref ||
    (conjugateConnection.linkedEntityHelmReference ?? null) === connectionSide.polymer_ref
  );
}

export function getRelatedAttachmentStyle(
  complexPolymerType: ComplexPolymerType,
  connectionSide: ComplexPolymerBondConnectionSide,
  conjugateConnectionMap: ConjugateConnectionMap,
  polymerRef: string
): ComplexPolymerAttachmentStyle | null {
  const maybeConjugateConnection = Object.values(conjugateConnectionMap).find(
    (conjugateConnection) =>
      conjugateConnection.helmReference !== polymerRef &&
      doesConnectionSideMatchConjugateConnectionForResolvedAttachment(
        complexPolymerType,
        connectionSide,
        conjugateConnection
      )
  );
  return maybeConjugateConnection?.attachmentStyle ?? null;
}

export function validateConnectionSideForSubmission(
  attachmentStyle: ComplexPolymerAttachmentStyle,
  connectionSide: ComplexPolymerBondConnectionSide
): void {
  if (getPolymerTypeFromRef(connectionSide.polymer_ref) === HELMPolymerType.RNA) {
    assert(
      connectionSide.residue_index != null &&
        connectionSide.modification_type != null &&
        (attachmentStyle === ComplexPolymerAttachmentStyle.DRAWN || connectionSide.r_group != null),
      'Backbone connection side must be fully specified to create connection'
    );
  } else {
    if (attachmentStyle === ComplexPolymerAttachmentStyle.DRAWN) {
      assert(connectionSide.r_group == null, 'Expected drawn attachment to not have R group populated');
      assert(
        getPolymerTypeFromRef(connectionSide.polymer_ref) === HELMPolymerType.CHEM,
        'Expected drawn attachment to be between CHEM polymers'
      );
    } else {
      assert(
        connectionSide.r_group != null,
        'Expected leaving group style attachment to have R group populated'
      );
    }
    assert(
      connectionSide.residue_index == null && connectionSide.modification_type == null,
      'Expected molecule connection side to not have residue index or modification type populated'
    );
  }
}

export function getMixedStyleAttachmentErrorMessage(
  relatedAttachmentStyle: ComplexPolymerAttachmentStyle
): string {
  const attachmentStyleString =
    relatedAttachmentStyle === ComplexPolymerAttachmentStyle.DRAWN ? 'Leaving group' : 'Drawn';
  const oppositeAttachmentStyleString =
    relatedAttachmentStyle === ComplexPolymerAttachmentStyle.DRAWN ? 'drawn' : 'leaving group';
  return `${attachmentStyleString} style attachment is currently unsupported to components with ${oppositeAttachmentStyleString} attachments.`;
}
